
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Hero } from "@/components/Hero";
import { Navigation } from "@/components/Navigation";
import { Services } from "@/components/Services";
import { UseCases } from "@/components/UseCases";
import { BlogSection } from "@/components/BlogSection";
import { FAQ } from "@/components/FAQ";
import { Footer } from "@/components/Footer";
import { ContactSection } from "@/components/ContactSection";
import { BlockRenderer, ContentBlock } from '@/components/blocks/BlockRenderer';
import { createSafeHTML } from '@/utils/sanitize';

interface FrontPageData {
  id: string;
  title: string;
  meta_title?: string;
  meta_description?: string;
  content_html?: string;
  content_json?: any;
  content_blocks: ContentBlock[];
}

const Index = () => {
  const [frontPage, setFrontPage] = useState<FrontPageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFrontPage = async () => {
      try {
        const { data, error } = await supabase
          .from('pages')
          .select('*')
          .eq('is_front_page', true)
          .eq('is_published', true)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setFrontPage({
            id: data.id,
            title: data.title,
            meta_title: data.meta_title,
            meta_description: data.meta_description,
            content_html: typeof data.content_json === 'object' && data.content_json ? 
              (data.content_json as any).html || '' : '',
            content_json: data.content_json,
            content_blocks: (data.content_blocks as unknown as ContentBlock[]) || [],
          });
        }
      } catch (err) {
        console.error('Error fetching front page:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFrontPage();
  }, []);

  // Title and meta description for homepage are controlled via static index.html to avoid JS mismatches.

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden="true"></div>
            <span className="text-gray-600">Loading...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // If there's a designated front page with content blocks, render it
  if (frontPage && frontPage.content_blocks && frontPage.content_blocks.length > 0) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        
        <main>
          {frontPage.content_blocks.map((block) => (
            <BlockRenderer key={block.id} block={block} />
          ))}
        </main>

        <ContactSection />
        <Footer />
      </div>
    );
  }

  // Always fall back to the original static homepage
  // If there's front page data with homepage fields, use that; otherwise use static content
  const homepageData = frontPage?.content_json as any;

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <Hero homepageData={homepageData} />
      <Services homepageData={homepageData} />
      <UseCases homepageData={homepageData} />
      <BlogSection />
      <FAQ homepageData={homepageData} />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default Index;
